import type { ReceiptSplitPlan } from "../../types/receipt";
import type { Dispatch, RefObject, SetStateAction } from "react";
import { ArrowUpRight } from "lucide-react";
import type { DashboardData, ExpenseInput, Owner } from "../../types";
import type { MooAction, MooMessage } from "../../types/moo";
import type { ConfirmState } from "../../components/ConfirmDialog";
import MooChart from "./MooChart";
import MooProposal from "./MooProposal";
import LoadingLabel from "../../components/LoadingLabel";
import { MooMarkdown } from "./MooMarkdown";
import MooMessagePhotos from "./MooMessagePhotos";
import type { ReceiptAttachment } from "../../lib/image-data";

// The conversation: what was asked, what the assistant found, and anything it is proposing to
// write. Proposals are why this is not a plain message list — each stays pending until it is
// accepted here, so nothing the assistant suggests reaches the ledger on its own.

export interface MooMessagesProps {
  /** Focused when a suggested prompt is used, so the cursor lands where typing continues. */
  composer: RefObject<HTMLTextAreaElement | null>;
  /** Scrolled to when the conversation grows, keeping the newest message in view. */
  end: RefObject<HTMLDivElement | null>;
  chatId: string;
  /** Called when a proposal is saved or withdrawn, so the ledger view refreshes. */
  onChanged: () => void;
  messages: MooMessage[];
  previews?: Record<string, ReceiptAttachment[]>;
  setMessages: Dispatch<SetStateAction<MooMessage[]>>;
  data: DashboardData;
  profile: Owner;
  busy: string;
  setError: (message: string) => void;
  /** Which proposals are open for editing, keyed by action id. */
  editing: Record<string, boolean>;
  setEditing: Dispatch<SetStateAction<Record<string, boolean>>>;
  setText: Dispatch<SetStateAction<string>>;
  revise: (m: MooMessage, index: number, expense?: ExpenseInput, item?: MooAction["item"], remove?: boolean, receiptSplit?: ReceiptSplitPlan, repairLinePrices?: boolean) => Promise<void>;
  save: (m: MooMessage) => Promise<void>;
  openTransaction: (id: string) => void;
  setConfirm: (state: ConfirmState | null) => void;
}

export default function MooMessages({
  messages, setMessages, data, profile, busy, setError, editing, setEditing, previews,
  setText, revise, save, openTransaction, setConfirm, composer, end, chatId, onChanged,
}: MooMessagesProps) {
  return (
    <div className="moo-messages">
      {!messages.length && !busy && (
        <div className="moo-empty">
          <h2>What’s happening in the barnyard?</h2>
          <p>
            Add an expense, track a price, or ask about your
            spending.
          </p>
          <div>
            {[
              "Show my expenses today",
              "Compare paper towel prices",
            ].map((s) => (
              <button
                key={s}
                onClick={() => {
                  setText(s);
                  composer.current?.focus();
                }}
              >
                {s}
                <ArrowUpRight size={16} />
              </button>
            ))}
          </div>
        </div>
      )}
      {messages.map((m) => (
        <article key={m.id} className={`moo-message ${m.role}`}>
          {m.role === "assistant" ? <MooMarkdown text={m.content} /> : <p className="moo-text">{m.content.replace(/\n?\[Receipt photos attached; saved receipts retain photos privately\]/g, "")}</p>}
          {m.role === "user" && <MooMessagePhotos chatId={chatId} message={m} local={previews?.[m.id]} />}
          {m.payload.chart && <MooChart chart={m.payload.chart} />}
          {!!m.payload.records?.length && (
            <details className="moo-records">
              <summary>
                View matching records ({m.payload.records.length})
              </summary>
              {m.payload.records.map((r) =>
                r.transactionId ? (
                  <button
                    key={r.id}
                    onClick={() => openTransaction(r.transactionId)}
                  >
                    {r.label}
                    <ArrowUpRight size={16} />
                  </button>
                ) : (
                  <p key={r.id}>{r.label} · Standalone</p>
                ),
              )}
            </details>
          )}
          {m.payload.actions && (
            <MooProposal
              m={m} data={data} profile={profile} busy={busy} editing={editing} setEditing={setEditing}
              setError={setError} revise={revise} save={save} openTransaction={openTransaction}
              setConfirm={setConfirm} chatId={chatId} setMessages={setMessages} onChanged={onChanged}
            />
          )}
          {m.payload.receipt && !m.payload.actions && (
            <details>
              <summary>
                Receipt items ({m.payload.receipt.items.length})
              </summary>
              {m.payload.receipt.items.map((i) => (
                <p key={i.id}>
                  {i.name || i.original} · {i.total}
                </p>
              ))}
            </details>
          )}
        </article>
      ))}
      {busy && (
        <p className="moo-working" role="status">
          <span />
          <LoadingLabel>{busy.replace(/(?:…|\.\.\.)$/, "")}</LoadingLabel>
        </p>
      )}
      <div ref={end} />
    </div>
  );
}
